import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTourContext } from './TourProvider';
import { TourTooltip } from './TourTooltip';
import { TourSheet } from './TourSheet';
import { useIsMobile } from '@/hooks/use-mobile';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

export function TourOverlay() {
  const { isActive, currentStepData } = useTourContext();
  const isMobile = useIsMobile();
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const updateTargetRect = useCallback(() => {
    if (!currentStepData) {
      setTargetRect(null);
      return;
    }

    const target = document.querySelector(currentStepData.targetSelector);
    if (target) {
      const rect = target.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        right: rect.right,
        bottom: rect.bottom,
      });
    } else {
      setTargetRect(null);
    }
  }, [currentStepData]);

  useEffect(() => {
    if (isActive) {
      updateTargetRect();
      
      const handleResize = () => updateTargetRect();
      const handleScroll = () => updateTargetRect();
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isActive, updateTargetRect]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive]);

  if (!mounted || !isActive) return null;

  const padding = 8;

  return createPortal(
    <AnimatePresence>
      {isActive && (
        <>
          {/* Backdrop with rectangular cutout */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[9998]"
            style={{
              background: 'rgba(0, 0, 0, 0.7)',
              ...(targetRect ? {
                clipPath: `polygon(
                  0% 0%, 0% 100%, 100% 100%, 100% 0%, 0% 0%,
                  ${targetRect.left - padding}px ${targetRect.top - padding}px,
                  ${targetRect.left - padding}px ${targetRect.bottom + padding}px,
                  ${targetRect.right + padding}px ${targetRect.bottom + padding}px,
                  ${targetRect.right + padding}px ${targetRect.top - padding}px,
                  ${targetRect.left - padding}px ${targetRect.top - padding}px
                )`,
              } : {}),
            }}
          />

          {/* Highlight border around target */}
          {targetRect && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="fixed z-[9999] pointer-events-none rounded-md animate-tour-glow"
              style={{
                top: targetRect.top - padding,
                left: targetRect.left - padding,
                width: targetRect.width + padding * 2,
                height: targetRect.height + padding * 2,
              }}
            />
          )}

          {/* Tooltip or Sheet */}
          {isMobile ? (
            <TourSheet />
          ) : (
            targetRect && <TourTooltip targetRect={targetRect} />
          )}
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
